<template>
  <div class="presenter-view">
    <div class="top-bar"/>
    <div class="map-wrapper">
      <GoogleMapPresenterView @boundsChange="onBoundsChange" @zoomChange="onZoomChange" ref="map-wrapper" v-if="dataWithCoordinates.length > 0" :presenterData="dataBasedOnZoom" :key="updateMap"/>
      <PresenterViewOverlay :presenterData="separatedData" :width="overlayWidth" :left="overlayLeft" :isMobile="isMobile"/>
      <PresenterViewFilters :countries="countryFilters" @onCountryFilter="changeCountryFilter" @onServerFilters="changeServerFilters" class="filters" :class="{'mobile': isMobile}"/>
      <PresenterViewInfoPopup v-if="showingWorldData" class="information-popup"/>
    </div>
  </div>
</template>

<script>
import GoogleMapPresenterView from '@/components/GoogleMapPresenterView'
import PresenterViewOverlay from '@/components/PresenterViewOverlay'
import PresenterViewFilters from '@/components/PresenterViewFilters'
import PresenterViewInfoPopup from '@/components/PresenterViewInfoPopup'
import {mapGetters} from 'vuex'

import {Socket} from 'phoenix-socket'

export default {
  components: {
    GoogleMapPresenterView,
    PresenterViewOverlay,
    PresenterViewFilters,
    PresenterViewInfoPopup
  },

  data() {
    return {
      title: 'Presenter View',
      overlayWidth: 700,
      overlayLeft: 30,
      countryZoom: 6,
      hospitals: [],
      updateMap: 0,
      presenterData: [],
      dataWithCoordinates: [],
      countriesDataWithCoordinates: [],
      separatedData: [],
      widthOverlayPrecentage: 0,
      visibleArea: {},
      currentZoom: 0,
      timeoutFunc: null,
      countryFilters: [],
      selectedCountries: [],
      screenWidth: 0,
      showingWorldData: false,
      filterObject: {}
    }
  },

  methods: {
    setNewData(data) {
      this.presenterData = data
      this.dataWithCoordinates = []
      this.countriesDataWithCoordinates = []
      this.countryFilters = []
      for(const currentData of this.presenterData) {
        if(currentData.hospital.latlng && currentData.hospital.latlng[0] && currentData.hospital.latlng[1]) {
          this.dataWithCoordinates.push(currentData)
          if(!this.countriesDataWithCoordinates[currentData.hospital.country.name]) {
            this.countryFilters.push(currentData.hospital.country.name)
            this.countriesDataWithCoordinates[currentData.hospital.country.name] = {
              patient_numbers: [],
              hospital: {},
              hospitals: []
            }
          }
          this.countriesDataWithCoordinates[currentData.hospital.country.name].hospitals.push(currentData)
        }
      }
      this.fillCountryCoordinates()
      this.separateData()
    },

    changeCountryFilter(selectedCountries) {
      this.selectedCountries = selectedCountries
      this.separateData()
    },

    changeServerFilters(selectedIOL, selectedDate) {
      this.filterObject = {}
      if(selectedIOL.length > 0) {
        this.filterObject.iol = selectedIOL.join()
      }
      if(selectedDate && selectedDate !== '') {
        this.filterObject.time_period = selectedDate
      }

      this.channel.push('filters', this.filterObject)
    },

    filterOneHospital(hospitalID) {
      this.channel.push('change-filters', {hospital_id: hospitalID, filters: this.filterObject})
    },

    resizeHandler() {
      this.screenWidth = window.innerWidth
      this.setWidthOverlayPrecentage()
    },

    setWidthOverlayPrecentage() {
      this.widthOverlayPrecentage = (this.overlayWidth + this.overlayLeft) / window.innerWidth
    },

    onZoomChange(zoom) {
      this.currentZoom = zoom
    },

    onBoundsChange(newBounds) {
      const leftLng = newBounds.getSouthWest().lng()
      const rightLng = newBounds.getNorthEast().lng()

      if(leftLng === -180 && rightLng === 180) {
        this.showingWorldData = true
        this.visibleArea = {
          leftLng: -180,
          rightLng: 180,
          topLat: 90,
          bottomLat: -90
        }
      } else {
        this.showingWorldData = false
        const visibleSize = this.calculateDistanceLng(leftLng, rightLng) * this.widthOverlayPrecentage

        this.visibleArea = {
          leftLng: this.screenWidth > 1100 ? this.addToLng(leftLng, visibleSize) : leftLng,
          rightLng: rightLng,
          topLat: newBounds.getNorthEast().lat(),
          bottomLat: newBounds.getSouthWest().lat()
        }
      }
      this.separateData()
    },

    calculateDistanceLng(lng1, lng2) {
      if(lng2 > lng1) {
        return lng2 - lng1
      }
      return 180 - lng1 + 180 + lng2
    },

    addToLng(lng, amount) {
      let result = lng + amount

      if(result >= 180) {
        result %= 180
        result -= 180
      }
      return result
    },

    separateData() {
      if(this.timeoutFunc !== null) {
        clearTimeout(this.timeoutFunc)
      }

      this.timeoutFunc = setTimeout(() => {
        this.separatedData = []
        if(this.currentZoom >= this.countryZoom) {
          for(const data of this.dataWithCoordinates) {
            if(this.selectedCountries.length !== 0 && !this.selectedCountries.includes(data.hospital.country.name)) {
              continue
            }
            const hospitalLatLng = data.hospital.latlng

            if(this.isHospitalInBounds(hospitalLatLng, this.visibleArea)) {
              this.separatedData.push(data)
            }
          }
        } else {
          for(const key in this.countriesDataWithCoordinates) {
            if(this.selectedCountries.length !== 0 && !this.selectedCountries.includes(key)) {
              continue
            }
            const data = this.countriesDataWithCoordinates[key]
            const hospitalLatLng = data.hospital.latlng

            if(this.isHospitalInBounds(hospitalLatLng, this.visibleArea)) {
              for(const singleHospitalData of data.hospitals) {
                this.separatedData.push(singleHospitalData)
              }
            }
          }
        }
      }, 200)
    },

    isHospitalInBounds(hospitalLatLng, bounds) {
      if(bounds.leftLng < bounds.rightLng) {
        return hospitalLatLng[1] > bounds.leftLng &&
          hospitalLatLng[1] < bounds.rightLng &&
          hospitalLatLng[0] < bounds.topLat &&
          hospitalLatLng[0] > bounds.bottomLat
      }
      return (hospitalLatLng[1] > bounds.leftLng ||
        hospitalLatLng[1] < bounds.rightLng) &&
        hospitalLatLng[0] < bounds.topLat &&
        hospitalLatLng[0] > bounds.bottomLat
    },

    fillCountryCoordinates() {
      for(const key in this.countriesDataWithCoordinates) {
        const countryData = this.countriesDataWithCoordinates[key]
        let lat = 0
        let lng = 0
        const totalPatientMap = {}
        const totalPatientNumbers = []

        for(const hospitalData of countryData.hospitals) {
          lat += hospitalData.hospital.latlng[0]
          lng += hospitalData.hospital.latlng[1]
          for(const patient of hospitalData.patient_numbers) {
            totalPatientMap[patient] = true
          }
        }

        for(const patientId in totalPatientMap) {
          totalPatientNumbers.push(patientId)
        }
        lat /= countryData.hospitals.length
        lng /= countryData.hospitals.length
        countryData.patient_numbers = totalPatientNumbers
        countryData.hospital = {latlng: [lat, lng]}
      }
    },

    updateHospitalData(hospitalData) {
      for(let i = 0; i < this.presenterData.length; i++) {
        if(this.presenterData[i].hospital.id === hospitalData.hospital.id) {
          this.presenterData[i] = hospitalData
          this.setNewData(this.presenterData)
          return
        }
      }
      this.presenterData.push(hospitalData)
      this.setNewData(this.presenterData)
    }
  },

  computed: {
    ...mapGetters(['getAuthToken']),

    isMobile() {
      return this.screenWidth <= 1100
    },

    dataBasedOnZoom() {
      const reformattedArray = []

      if(this.currentZoom >= this.countryZoom) {
        if(this.selectedCountries.length !== 0) {
          for(const data of this.dataWithCoordinates) {
            if(this.selectedCountries.includes(data.hospital.country.name)) {
              reformattedArray.push(data)
            }
          }
          return reformattedArray
        }
        return this.dataWithCoordinates
      }
      for(const key in this.countriesDataWithCoordinates) {
        if(this.selectedCountries.length !== 0 && !this.selectedCountries.includes(key)) {
          continue
        }
        reformattedArray.push({
          patient_numbers: this.countriesDataWithCoordinates[key].patient_numbers,
          hospital: this.countriesDataWithCoordinates[key].hospital
        })
      }
      return reformattedArray
    }
  },

  mounted() {
    this.screenWidth = window.innerWidth
    this.setWidthOverlayPrecentage()

    const socket = new Socket('/socket', {params: {token: this.getAuthToken}})

    socket.connect()
    this.channel = socket.channel('hospitals:admin')
    this.channel.join()
      .receive('ok', (resp) => { 
        this.channel.push('filters', {})
        // this.setNewData(resp.data) 
      })
      .receive('error', (resp) => { console.log(resp); })
      .receive('timeout', () => { console.log('TIMEOUT'); })

    this.channel.on('change', (hospitalData) => {
      if(this.filterObject.iol || this.filterObject.time_period) {
        this.filterOneHospital(hospitalData.hospital.id)
      } else {
        this.updateHospitalData(hospitalData)
      }
    })

    this.channel.on('filters', (res) => {
      this.setNewData(res.data)
    })

    this.channel.on('change-filters', (hospitalData) => {
      this.updateHospitalData(hospitalData)
    })
  },

  created() {
    window.addEventListener('resize', this.resizeHandler)
  },

  destroyed() {
    window.removeEventListener('resize', this.resizeHandler)
  }
}
</script>

<style lang="sass" scoped>
@import 'raypro_ui/src/styles/base/_variables.sass'
@import 'raypro_ui/src/styles/helpers/_mixins.sass'

.presenter-view
  height: 100%
  display: flex
  flex-direction: column

  .top-bar
    width: 100%
    height: 10px
    background-color: $color-primary

  .map-wrapper
    flex-grow: 1
    position: relative

    .filters
      position: absolute
      top: 30px
      right: 30px

      &.mobile
        top: 10px
        right: 10px

    .information-popup
      pointer-events: none
      position: absolute
      top: 30px
      left: 0
      right: 0
      margin: 0 auto
        
</style>



// WEBPACK FOOTER //
// src/components/pages/PresenterView/PresenterView.vue